/**
 * Which `Invoice` documents an order currently has, per side.
 *
 * A normal order has one invoice. A `'split'` order has two, each its own independent invoice: the
 * customer side's (billed to the customer, held in `Order.customerInvoiceID`) and the partner side's
 * (billed to the insurance partner, held in `Order.partnerInvoiceID`, and carrying `partnerID`).
 * A partner-billed (non-split) order has one invoice that carries `partnerID` but sits in the
 * customer slot — only a split ever uses `partnerInvoiceID`.
 *
 * Mirrors `loadInvoiceOrFailedPlaceholder` in lab-cloud: the invoice a side's pointer references, or
 * — when it has no pointer — the failed pre-flight placeholder that covers it (a pre-flight failure
 * never claims the order, so the failure is found through the invoice's own `orderIDs`).
 */
import type { IInvoice } from '@team-milva/lab-models/invoice'
import type { IOrder } from '@team-milva/lab-models/resources'

export type TInvoiceSide = 'customer' | 'partner'

type TSideInvoice = Pick<IInvoice, 'id' | 'type' | 'statuses' | 'credit' | 'timestamps'> & { partnerID?: string }

/** The invoice currently held by one side of a split order, or `undefined` when it has none. */
export function resolveSplitSideInvoice<T extends TSideInvoice>(
  order: Pick<IOrder, 'customerInvoiceID' | 'partnerInvoiceID'>,
  invoicesOfOrder: T[],
  side: TInvoiceSide
): T | undefined {
  const sideInvoices = invoicesOfOrder.filter((x) => Boolean(x.partnerID) === (side === 'partner'))
  const pointerID = side === 'partner' ? order.partnerInvoiceID : order.customerInvoiceID
  const pointed = pointerID ? sideInvoices.find((x) => x.id === pointerID) : undefined
  if (pointed) {
    return pointed
  }
  return sideInvoices
    .filter((x) => x.type === 'business-central' && x.statuses.sync === 'failed' && !x.credit)
    .sort((a, b) => (b.timestamps.createdAt ?? 0) - (a.timestamps.createdAt ?? 0))[0]
}

/** Invoices of one side that were credited in full and so no longer belong to the order — newest first. */
export function resolveSplitSideCreditedInvoices<T extends TSideInvoice>(
  invoicesOfOrder: T[],
  side: TInvoiceSide,
  currentInvoiceID: string | undefined
): T[] {
  const sortKey = (x: T): number =>
    x.credit?.timestamps.completedAt ?? x.credit?.timestamps.createdAt ?? x.timestamps.createdAt ?? 0
  return invoicesOfOrder
    .filter(
      (x) =>
        Boolean(x.partnerID) === (side === 'partner') &&
        x.credit?.status === 'completed' &&
        x.id !== currentInvoiceID
    )
    .sort((a, b) => sortKey(b) - sortKey(a))
}

/** Whether a side's invoice has actually gone out: a manual "mark as sent", or a posted BC invoice whose send was confirmed. */
export function isSideInvoiceSent(invoice: Pick<IInvoice, 'type' | 'statuses' | 'result'> | undefined): boolean {
  if (!invoice) {
    return false
  }
  if (invoice.type === 'manual') {
    return true
  }
  return Boolean(invoice.result.invoiceNumber) && invoice.statuses.sync === 'completed'
}

/**
 * How loudly an invoice's sync state needs the user's attention — decides which of two live invoices
 * of one order (a split order's customer side and partner side) an order-level chip reads. A failed
 * side must never be hidden behind a healthy one.
 */
const SYNC_SEVERITY: Record<string, number> = {
  failed: 4,
  'posted-not-sent': 3,
  'in-progress': 2,
  pending: 1,
  completed: 0,
}

export function invoiceSyncSeverity(invoice: Pick<IInvoice, 'statuses'>): number {
  return SYNC_SEVERITY[invoice.statuses.sync] ?? 0
}

/** The invoice needing the most attention — the first one on a tie, so a normal order's only invoice is itself. */
export function pickMostUrgentInvoice<T extends Pick<IInvoice, 'statuses'>>(invoices: T[]): T | undefined {
  let best: T | undefined
  for (const invoice of invoices) {
    if (!best || invoiceSyncSeverity(invoice) > invoiceSyncSeverity(best)) {
      best = invoice
    }
  }
  return best
}

/**
 * Whether every live invoice of an order was finished at its draft. The order-level chip says
 * "Kladde oprettet" only then: with one booked invoice among them the order is simply done
 * ("Afsluttet"), and which invoice happens to come first must not decide that.
 */
export function areAllInvoicesDraftOnly(invoices: { result: { isDraftOnly?: boolean } }[]): boolean {
  return invoices.length > 0 && invoices.every((x) => x.result.isDraftOnly === true)
}
